import { Link } from 'react-router-dom';
import {
  Rocket,
  Target,
  Users,
  Sparkles,
  AlertTriangle,
  Clock,
  TrendingUp,
  Calendar,
  Heart,
  CheckCircle,
  XCircle,
  Mail,
  Linkedin,
  Hash,
  Facebook,
} from 'lucide-react';

export default function OutreachPlaybook() {
  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <div className="inline-flex items-center gap-2 bg-sage/10 text-sage px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest mb-6 border border-sage/20">
          <Rocket className="w-4 h-4" /> Internal Growth Strategy
        </div>
        <h1 className="text-4xl font-black text-stone-900 dark:text-stone-100 mb-4">The Growth Playbook</h1>
        <p className="text-stone-600 dark:text-stone-400 text-lg leading-relaxed max-w-2xl">
          Acquiring massage studios and spas who are struggling with scheduling chaos. From "couples booking nightmare" to "everything just works" through value-first outreach.
        </p>

        <div className="mt-8 p-4 bg-stone-100 dark:bg-stone-800 rounded-2xl flex flex-col md:flex-row gap-6 items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-sage rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] font-black text-sage uppercase tracking-widest">Beta Goal</div>
              <div className="text-lg font-bold text-stone-900 dark:text-stone-100">10 Studio Owners Testing Free → Product Feedback → First Paid Customers</div>
            </div>
          </div>
        </div>
      </div>

      {/* ICP Section */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Ideal Customer Profile (ICP)</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">We target massage studios that offer couples services but struggle with scheduling logistics.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-6">
            <h4 className="font-black text-xs uppercase text-sage mb-4 tracking-widest flex items-center gap-2">
              <Heart className="w-4 h-4" /> Primary: Massage Studios & Spas
            </h4>
            <p className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-2">Massage Studios, Day Spas, Wellness Centers</p>
            <ul className="text-xs text-stone-600 dark:text-stone-400 space-y-2 mt-4">
              <li>• Size: 1-10 therapists</li>
              <li>• Currently offering couples massages OR interested in adding them</li>
              <li>• Signal: "Couples booking is a nightmare to coordinate"</li>
            </ul>
          </div>
          <div className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-6">
            <h4 className="font-black text-xs uppercase text-emerald-600 dark:text-emerald-400 mb-4 tracking-widest flex items-center gap-2">
              <Users className="w-4 h-4" /> Current Tools They Use
            </h4>
            <p className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-2">Spreadsheets, Google Calendar, Generic Booking Software</p>
            <ul className="text-xs text-stone-600 dark:text-stone-400 space-y-2 mt-4">
              <li>• Acuity, Calendly (not spa-specific)</li>
              <li>• Vagaro, MindBody (overly complex, expensive)</li>
              <li>• Pain: "Software doesn't understand spa workflows"</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">Pain Point Signals</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-4 p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            <p className="text-sm font-bold text-rose-700 dark:text-rose-300">"Couples booking is a nightmare to coordinate."</p>
          </div>
          <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <Clock className="w-5 h-5 text-amber-500" />
            <p className="text-sm font-bold text-amber-700 dark:text-amber-300">"I spend hours on end-of-day reports and admin work."</p>
          </div>
          <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <p className="text-sm font-bold text-blue-700 dark:text-blue-300">"No-shows are killing my revenue and I can't track them."</p>
          </div>
          <div className="flex items-center gap-4 p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
            <Calendar className="w-5 h-5 text-purple-500" />
            <p className="text-sm font-bold text-purple-700 dark:text-purple-300">"Double-booking rooms and therapists constantly."</p>
          </div>
        </div>
      </div>

      {/* Where They Congregate */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Where They Congregate</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">Go where massage studio owners discuss operations and growth challenges.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-xs text-stone-500 uppercase mb-4 tracking-widest flex items-center gap-2">
              <Hash className="w-4 h-4" /> Reddit
            </h5>
            <ul className="text-sm font-bold text-stone-900 dark:text-stone-100 space-y-2">
              <li>r/massage</li>
              <li>r/MassageTherapists</li>
              <li>r/smallbusiness</li>
              <li>r/Entrepreneur</li>
            </ul>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-xs text-stone-500 uppercase mb-4 tracking-widest flex items-center gap-2">
              <Facebook className="w-4 h-4" /> Facebook Groups
            </h5>
            <ul className="text-sm font-bold text-stone-900 dark:text-stone-100 space-y-2">
              <li>Massage Therapist Business Owners</li>
              <li>Spa & Salon Owners</li>
              <li>Mobile Massage Therapists</li>
              <li>Local spa/wellness groups</li>
            </ul>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-xs text-stone-500 uppercase mb-4 tracking-widest flex items-center gap-2">
              <Linkedin className="w-4 h-4" /> LinkedIn
            </h5>
            <ul className="text-sm font-bold text-stone-900 dark:text-stone-100 space-y-2">
              <li>Wellness industry groups</li>
              <li>Small business communities</li>
              <li>Search: "massage studio owner"</li>
              <li>Search: "spa manager"</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Engagement Templates */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-6 border-l-4 border-sage pl-4">Engagement Templates</h2>

        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">Template A: Pain Point Response</h3>
            <p className="text-xs text-stone-500 italic mb-3">Best for: Responding to someone complaining about scheduling issues in a thread. Highest success rate.</p>
            <div className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-5 font-mono text-sm text-stone-700 dark:text-stone-300 relative">
              <div className="absolute -top-2 right-4 bg-sage text-white text-[10px] font-black px-3 py-1 rounded">COPY TEMPLATE</div>
              <p>Hey [Name]!</p>
              <br />
              <p>I saw your post about [specific pain point - e.g., "scheduling headaches with couples massages"].</p>
              <br />
              <p>I'm actually building a scheduling tool specifically for massage studios - it's designed to handle the exact issues you're describing (couples room matching, therapist pairing, no-show tracking).</p>
              <br />
              <p>We're in early beta and looking for studio owners to test it free and give feedback. Would you be open to a quick 15-min call to see if it's a fit?</p>
              <br />
              <p>Here's my calendar if easier: https://calendly.com/serenetouch/30min</p>
              <br />
              <p>Either way, hope you get that sorted!</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">Template B: Cold Outreach</h3>
            <p className="text-xs text-stone-500 italic mb-3">Best for: Direct outreach to identified studio owners from their profile/website.</p>
            <div className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-5 font-mono text-sm text-stone-700 dark:text-stone-300 relative">
              <div className="absolute -top-2 right-4 bg-sage text-white text-[10px] font-black px-3 py-1 rounded">COPY TEMPLATE</div>
              <p>Hi [Name],</p>
              <br />
              <p>I came across [Studio Name] and love what you're doing with [something specific - couples packages, wellness focus, etc.].</p>
              <br />
              <p>Quick question: how are you currently handling couples massage bookings? I ask because I'm building SereneTouch - scheduling software made specifically for massage studios.</p>
              <br />
              <p>We're looking for 10 studio owners to test the MVP free and help shape the product. In exchange for 20 mins of your feedback, you'd get:</p>
              <p>• Free access during beta</p>
              <p>• Direct input on features</p>
              <p>• Locked-in early adopter pricing</p>
              <br />
              <p>Interested in a quick call? https://calendly.com/serenetouch/30min</p>
              <br />
              <p>Cheers,<br />[Your Name]</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">Template C: Short & Direct</h3>
            <p className="text-xs text-stone-500 italic mb-3">Best for: Warm leads who've already engaged with your content/comments.</p>
            <div className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-5 font-mono text-sm text-stone-700 dark:text-stone-300 relative">
              <div className="absolute -top-2 right-4 bg-sage text-white text-[10px] font-black px-3 py-1 rounded">COPY TEMPLATE</div>
              <p>Hey [Name] - saw you commenting about [topic].</p>
              <br />
              <p>Building a scheduling tool just for massage studios. Looking for beta testers who deal with couples bookings.</p>
              <br />
              <p>15 min call to see if it helps? https://calendly.com/serenetouch/30min</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-2">Template D: Facebook Group Comment</h3>
            <p className="text-xs text-stone-500 italic mb-3">Best for: Responding to posts about scheduling or staffing issues. Value-first approach.</p>
            <div className="bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl p-5 font-mono text-sm text-stone-700 dark:text-stone-300 relative">
              <div className="absolute -top-2 right-4 bg-sage text-white text-[10px] font-black px-3 py-1 rounded">COPY TEMPLATE</div>
              <p>This is such a common challenge! A few things that work:</p>
              <br />
              <p>1. Track your actual no-show rate (most studios are shocked when they see the number)</p>
              <p>2. For couples bookings, always confirm both therapists + room are available at the same time</p>
              <p>3. Consider spa-specific scheduling tools - they've gotten really good for exactly this</p>
              <br />
              <p>Happy to share more specifics if helpful. What type of services are you mainly booking?</p>
            </div>
          </div>
        </div>
      </div>

      {/* Positioning */}
      <div className="bg-gradient-to-br from-sage to-sage/80 rounded-xl p-6">
        <h2 className="text-2xl font-black text-white mb-6">Why SereneTouch?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-sage-100 text-[10px] font-black uppercase tracking-widest mb-2 opacity-80">Vs. Spreadsheets/Google Calendar</div>
            <p className="text-sm text-white/90">Manual, error-prone, no automation. Can't handle couples booking logic or therapist-room matching at all.</p>
          </div>
          <div>
            <div className="text-sage-100 text-[10px] font-black uppercase tracking-widest mb-2 opacity-80">Vs. Acuity/Calendly</div>
            <p className="text-sm text-white/90">Generic booking tools. Don't understand spa workflows or couples massage coordination. Clients need accounts.</p>
          </div>
          <div>
            <div className="text-sage-100 text-[10px] font-black uppercase tracking-widest mb-2 opacity-80">Vs. MindBody/Vagaro</div>
            <p className="text-sm text-white/90">Overly complex, expensive, not spa-focused. Learning curve is steep and features you don't need add clutter.</p>
          </div>
          <div>
            <div className="text-sage-100 text-[10px] font-black uppercase tracking-widest mb-2 opacity-80">Built FOR Massage Studios</div>
            <p className="text-sm text-white/90">Smart couples booking, automatic room + therapist matching, no client accounts required, end-of-day reports in one click.</p>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/20">
          <p className="text-center italic text-white/90">
            "The only scheduling software designed specifically for massage studios - with built-in couples booking, room matching, and spa-specific reporting."
          </p>
        </div>
      </div>

      {/* TOUCH Method */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">The TOUCH Method</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">Follow this framework for converting prospects into beta testers.</p>
        <div className="space-y-4">
          {[
            { letter: 'T', title: 'Target', desc: 'Confirm they own/manage a massage studio with 1-10 therapists. "Do you do couples massages?"' },
            { letter: 'O', title: 'Observe', desc: 'Listen for pain signals. "What are you currently using for scheduling? What\'s your biggest headache?"' },
            { letter: 'U', title: 'Understand', desc: 'Quantify the pain. "How much time do you spend on admin work weekly? How often do double-bookings happen?"' },
            { letter: 'C', title: 'Compare', desc: 'Position against alternatives. "Unlike generic tools, SereneTouch was built specifically for spa workflows."' },
            { letter: 'H', title: 'Hook', desc: 'Offer the demo. "Want to see how couples booking would work for your studio? Takes 15 minutes."' },
          ].map(({ letter, title, desc }) => (
            <div key={letter} className="flex gap-4">
              <div className="font-black text-sage text-2xl w-8">{letter}</div>
              <div>
                <div className="font-bold text-stone-900 dark:text-stone-100">{title}</div>
                <p className="text-sm text-stone-600 dark:text-stone-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Qualification */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Qualification Framework</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">All 5 criteria required before counting someone as a qualified lead.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="p-6 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
            <h4 className="font-black text-xs text-emerald-600 dark:text-emerald-400 uppercase mb-4 tracking-widest flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Must-Have Criteria
            </h4>
            <ul className="text-sm font-bold space-y-3 text-emerald-700 dark:text-emerald-300">
              <li>1. Owns/manages a massage studio (not just solo therapist)</li>
              <li>2. Does couples massages or wants to add them</li>
              <li>3. Currently using some scheduling solution</li>
              <li>4. Has active scheduling pain point</li>
              <li>5. Open to trying new software</li>
            </ul>
          </div>
          <div className="p-6 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <h4 className="font-black text-xs text-rose-600 dark:text-rose-400 uppercase mb-4 tracking-widest flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Disqualify If
            </h4>
            <ul className="text-sm font-bold space-y-3 text-rose-700 dark:text-rose-300">
              <li>• Solo mobile therapist with no studio/rooms</li>
              <li>• Happy with current software, not looking to switch</li>
              <li>• Not a decision-maker (just a therapist)</li>
              <li>• Outside US (for now)</li>
              <li>• "Just looking around" with no urgency</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">Red Flags</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <span className="px-2 py-1 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-xs font-medium rounded">Avoid</span>
            <span className="text-stone-600 dark:text-stone-400">"Can you build X custom feature?" (before even seeing the product)</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <span className="px-2 py-1 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-xs font-medium rounded">Avoid</span>
            <span className="text-stone-600 dark:text-stone-400">"What's your pricing?" (before understanding the value)</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
            <span className="px-2 py-1 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-xs font-medium rounded">Avoid</span>
            <span className="text-stone-600 dark:text-stone-400">"Just looking around" with no timeline or urgency</span>
          </div>
        </div>
      </div>

      {/* Common Objections */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Common Objections & Responses</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">How to handle the most frequent pushbacks from prospects.</p>

        <div className="space-y-4">
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-sm text-stone-900 dark:text-stone-100 mb-2">"How much does it cost?"</h5>
            <p className="text-sm text-stone-600 dark:text-stone-400">"We're in early beta right now, so it's completely free for testers. After beta, pricing will start around $49/month, but early testers get locked-in discounted rates."</p>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-sm text-stone-900 dark:text-stone-100 mb-2">"Is this just another Acuity/MindBody?"</h5>
            <p className="text-sm text-stone-600 dark:text-stone-400">"No - those are generic booking tools adapted for spas. SereneTouch is built from scratch specifically for massage studios. The couples booking logic alone is something they can't do."</p>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-sm text-stone-900 dark:text-stone-100 mb-2">"When will it be ready?"</h5>
            <p className="text-sm text-stone-600 dark:text-stone-400">"The MVP is ready now for testing. We're looking for 10 studio owners to test it and give feedback to make sure it solves real problems before we launch publicly."</p>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-sm text-stone-900 dark:text-stone-100 mb-2">"Do I need to be tech-savvy?"</h5>
            <p className="text-sm text-stone-600 dark:text-stone-400">"Not at all. If you can use Google Calendar, you can use SereneTouch. We designed it to be intuitive for busy studio owners."</p>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-sm text-stone-900 dark:text-stone-100 mb-2">"What if I have a unique workflow?"</h5>
            <p className="text-sm text-stone-600 dark:text-stone-400">"That's exactly why we're doing beta testing - to understand different workflows and make sure SereneTouch works for everyone. Your feedback would be invaluable."</p>
          </div>
          <div className="p-5 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-sm text-stone-900 dark:text-stone-100 mb-2">"Can it integrate with my payment processor?"</h5>
            <p className="text-sm text-stone-600 dark:text-stone-400">"We're building integrations now. What are you currently using?" (Get this info - helps with product development)</p>
          </div>
        </div>
      </div>

      {/* Engagement Strategy */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Engagement Strategy</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">The value-first approach to building relationships before pitching.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="p-6 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
            <h4 className="font-black text-xs text-emerald-600 dark:text-emerald-400 uppercase mb-4 tracking-widest">Do</h4>
            <ul className="text-sm space-y-3 text-emerald-700 dark:text-emerald-300">
              <li>• Engage genuinely BEFORE DMing (comment on 2-3 threads)</li>
              <li>• Personalize every message (mention studio name, specific pain)</li>
              <li>• Be helpful even if they're not interested</li>
              <li>• Wait 24-48 hours after engaging before DMing</li>
              <li>• Track what works (note which templates get responses)</li>
            </ul>
          </div>
          <div className="p-6 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <h4 className="font-black text-xs text-rose-600 dark:text-rose-400 uppercase mb-4 tracking-widest">Don't</h4>
            <ul className="text-sm space-y-3 text-rose-700 dark:text-rose-300">
              <li>• Mass DM without prior engagement</li>
              <li>• Post promotional content directly in groups</li>
              <li>• Be pushy or salesy in tone</li>
              <li>• Spam the same message to multiple people</li>
              <li>• Argue with skeptics (move on instead)</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">The 5-Step Process</h3>
        <div className="space-y-3">
          {[
            { num: 1, title: 'FIND', desc: 'Identify threads/posts with pain points about scheduling' },
            { num: 2, title: 'ENGAGE', desc: 'Comment helpfully with genuine advice (no pitch)' },
            { num: 3, title: 'WAIT', desc: '24-48 hours - let them see you\'re a real community member' },
            { num: 4, title: 'DM', desc: 'Personalized message using appropriate template' },
            { num: 5, title: 'QUALIFY', desc: 'Confirm all 5 must-have criteria before counting as lead' },
          ].map(({ num, title, desc }) => (
            <div key={num} className="flex items-center gap-4 p-4 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
              <div className="w-10 h-10 bg-sage rounded-lg flex items-center justify-center font-black text-white">{num}</div>
              <div>
                <p className="font-bold text-stone-900 dark:text-stone-100">{title}</p>
                <p className="text-xs text-stone-600 dark:text-stone-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Demo Flow */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Demo Flow</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">Structure your 15-min demo for maximum impact.</p>

        <div className="space-y-4">
          {[
            { step: '1', title: 'Discovery (3 min)', desc: 'Ask about current tools, couples booking frequency, biggest scheduling headaches. "Tell me about the last time you had a double-booking."' },
            { step: '2', title: 'Live Demo (5 min)', desc: 'Show the calendar, create a couples booking in real-time. Demonstrate automatic room + therapist matching.' },
            { step: '3', title: 'Features Tour (4 min)', desc: 'End-of-day reports, no-show tracking, guest booking without accounts. "Here\'s exactly how it would look for [Studio Name]."' },
            { step: '4', title: 'Value Discussion (2 min)', desc: 'Highlight time saved. "Most owners save 10+ hours/week on admin. What would you do with that time?"' },
            { step: '5', title: 'Next Steps (1 min)', desc: 'Set up beta access, schedule onboarding, or book follow-up. Always have a clear next action.' },
          ].map(({ step, title, desc }) => (
            <div key={step} className="flex gap-4 p-4 rounded-xl bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
              <div className="w-10 h-10 bg-sage rounded-lg flex items-center justify-center font-black text-white flex-shrink-0">{step}</div>
              <div>
                <div className="font-bold text-stone-900 dark:text-stone-100">{title}</div>
                <p className="text-sm text-stone-600 dark:text-stone-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lead Tracking */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Lead Tracking</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">Use the <Link to="/leads" className="text-sage hover:underline font-semibold">Leads</Link> page in the admin portal to track all prospects.</p>

        <Link to="/leads" className="block p-4 bg-sage/10 rounded-xl border border-sage/20 mb-6 hover:bg-sage/20 transition-colors">
          <h4 className="font-bold text-stone-900 dark:text-stone-100 mb-2 flex items-center gap-2">
            <Target className="w-4 h-4 text-sage" /> Go to Leads Page →
          </h4>
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-3">
            Click here to open the Leads CRM. It tracks:
          </p>
          <ul className="text-sm text-stone-600 dark:text-stone-400 space-y-1 ml-4">
            <li>• Contact name, email, phone, and studio name</li>
            <li>• Lead source (Reddit, Facebook, LinkedIn, Referral, etc.)</li>
            <li>• Pain points and notes</li>
            <li>• Lead score (Hot/Warm/Cold) with visual color coding</li>
            <li>• Status pipeline: New → Contacted → Demo Scheduled → Demo Done → Converted</li>
            <li>• Follow-up dates with overdue highlighting</li>
            <li>• Demo scheduling with date/time tracking</li>
            <li>• Activity timeline for each lead</li>
          </ul>
        </Link>

        <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mb-4">Lead Scoring</h3>
        <p className="text-sm text-stone-500 mb-4">Set the score when creating or editing a lead. The Leads page shows color-coded badges.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <h5 className="font-black text-xs text-rose-600 dark:text-rose-400 uppercase mb-2">🔥 Hot Lead</h5>
            <p className="text-xs text-rose-700 dark:text-rose-300">Follow up immediately. Decision-maker with active couples booking pain, using inadequate solution, willing to test ASAP.</p>
          </div>
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <h5 className="font-black text-xs text-amber-600 dark:text-amber-400 uppercase mb-2">🌡️ Warm Lead</h5>
            <p className="text-xs text-amber-700 dark:text-amber-300">Follow up within 48 hours. Interested but not urgent, decision-maker but needs convincing, pain exists but low severity.</p>
          </div>
          <div className="p-4 rounded-xl bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700">
            <h5 className="font-black text-xs text-stone-600 dark:text-stone-400 uppercase mb-2">❄️ Cold Lead</h5>
            <p className="text-xs text-stone-600 dark:text-stone-400">Low priority. Just gathering information, no immediate plans to switch, influencer but not decision-maker.</p>
          </div>
        </div>

        <h3 className="text-lg font-bold text-stone-900 dark:text-stone-100 mt-8 mb-4">Status Pipeline</h3>
        <p className="text-sm text-stone-500 mb-4">Move leads through these stages as you progress the relationship.</p>
        <div className="flex flex-wrap gap-2">
          <span className="px-3 py-1.5 bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 rounded-full text-xs font-medium">New</span>
          <span className="text-stone-400">→</span>
          <span className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">Contacted</span>
          <span className="text-stone-400">→</span>
          <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">Demo Scheduled</span>
          <span className="text-stone-400">→</span>
          <span className="px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs font-medium">Demo Done</span>
          <span className="text-stone-400">→</span>
          <span className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">Converted</span>
        </div>
      </div>

      {/* Weekly Metrics */}
      <div className="bg-white dark:bg-stone-900 rounded-xl shadow-sm p-6 border border-stone-200 dark:border-stone-800">
        <h2 className="text-2xl font-black text-stone-900 dark:text-stone-100 mb-2 border-l-4 border-sage pl-4">Weekly Goals & Metrics</h2>
        <p className="text-stone-600 dark:text-stone-400 mb-6 pl-5">Track these numbers to measure outreach effectiveness.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-sage/10 border border-sage/20 text-center">
            <div className="text-3xl font-black text-sage">10-15</div>
            <div className="text-sm text-stone-600 dark:text-stone-400 mt-1">Qualified Leads Identified</div>
          </div>
          <div className="p-5 rounded-xl bg-sage/10 border border-sage/20 text-center">
            <div className="text-3xl font-black text-sage">3</div>
            <div className="text-sm text-stone-600 dark:text-stone-400 mt-1">Demos Booked on Calendly</div>
          </div>
          <div className="p-5 rounded-xl bg-sage/10 border border-sage/20 text-center">
            <div className="text-3xl font-black text-sage">20%+</div>
            <div className="text-sm text-stone-600 dark:text-stone-400 mt-1">DM Response Rate</div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-stone-50 dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700">
          <h4 className="font-bold text-stone-900 dark:text-stone-100 mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Demo Calendar
          </h4>
          <a href="https://calendly.com/serenetouch/30min" target="_blank" rel="noopener noreferrer" className="text-sage hover:underline font-mono text-sm">
            https://calendly.com/serenetouch/30min
          </a>
        </div>
      </div>

      {/* Quick Reference */}
      <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl p-6">
        <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-2">
          <Sparkles className="w-6 h-6" /> Quick Reference Card
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sage text-[10px] font-black uppercase tracking-widest mb-3">The One-Liner</h4>
            <p className="text-white/90 text-sm italic">
              "SereneTouch is the only scheduling software designed specifically for massage studios - with built-in couples booking, room matching, and spa-specific reporting."
            </p>
          </div>
          <div>
            <h4 className="text-sage text-[10px] font-black uppercase tracking-widest mb-3">Core Differentiators</h4>
            <ul className="text-white/80 text-sm space-y-1">
              <li>• Built FOR massage studios (not generic adapted)</li>
              <li>• Couples booking with auto room + therapist matching</li>
              <li>• No client accounts required (guest booking)</li>
              <li>• End-of-day reports in one click</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10">
          <h4 className="text-sage text-[10px] font-black uppercase tracking-widest mb-3">Quick Qualification Checklist</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-white text-xs">Studio owner/manager</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-white text-xs">Does couples massages</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-white text-xs">Currently scheduling</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-white text-xs">Has active pain</div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 text-center">
              <div className="text-white text-xs">Open to trying</div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8 border-t border-stone-200 dark:border-stone-800">
        <p className="text-xs font-black text-stone-400 uppercase tracking-[0.3em]">
          Internal Strategy Asset - SereneTouch
        </p>
        <p className="text-xs text-stone-400 mt-2">
          Value first, personalization always, qualify thoroughly.
        </p>
      </div>
    </div>
  );
}
